import { File as FileEntity } from "@server/entities";
import { useEffect, useMemo, useRef, useState } from "react";
import WaveformData from "waveform-data";

export type PeaksStatus = "loading" | "ready" | "failed";

/**
 * Fetches the precomputed waveform peaks (.dat) for a file and exposes a
 * `resample(width)` helper the drawing component can call to get per-pixel
 * min/max sample arrays. Replaces downloading + decoding the full audio
 * file just to draw a picture of it.
 */
export const usePeaks = (
  file?: Pick<FileEntity, "peaksUrl" | "peaksStatus">
) => {
  const [status, setStatus] = useState<PeaksStatus>("loading");
  const dataRef = useRef<WaveformData | null>(null);

  const peaksUrl = file?.peaksUrl;
  const peaksStatus = file?.peaksStatus;

  const loggedZoomErrorRef = useRef(false);

  useEffect(() => {
    // Scoped to this effect run: a stale run's `cancelled` flag never
    // affects the current run's promise chain, so unmounting mid-fetch
    // or switching `file` while a fetch is in flight can't clobber state
    // that belongs to the new run.
    let cancelled = false;
    dataRef.current = null;
    loggedZoomErrorRef.current = false;

    if (peaksStatus === "failed") {
      setStatus("failed");
      return;
    }

    if (peaksStatus !== "ready" || !peaksUrl) {
      setStatus("loading");
      return;
    }

    setStatus("loading");

    fetch(peaksUrl)
      .then((response) => {
        if (!response.ok)
          throw new Error(`Peaks fetch failed: ${response.status}`);
        return response.arrayBuffer();
      })
      .then((buffer) => {
        if (cancelled) return;
        // WaveformData.create throws on malformed binary data; that
        // rejection is routed to the catch below, same as a network
        // failure or a non-2xx response, so every failure path ends in
        // status "failed" rather than leaving the hook stuck loading.
        dataRef.current = WaveformData.create(buffer);
        setStatus("ready");
      })
      .catch(() => {
        if (cancelled) return;
        setStatus("failed");
      });

    return () => {
      cancelled = true;
    };
  }, [peaksUrl, peaksStatus]);

  // `resample` reads dataRef.current at CALL time, not at closure-creation
  // time, so it can never be a stale closure over old data — refs are
  // always live. The `[status]` dependency isn't there to keep the data
  // fresh (it's already fresh); it's there so `resample`'s identity
  // changes when new data arrives, letting a consumer that depends on
  // `resample` (e.g. `useEffect(() => draw(resample(w)), [resample])`)
  // know it should redraw.
  const resample = useMemo(
    () => (width: number) => {
      const data = dataRef.current;
      if (!data || width <= 0) return null;

      // A `null` return here means "cannot draw at this width" — distinct
      // from "no data yet" (which is reflected by `status` instead).
      // waveform-data's resample() throws (doesn't degrade) when the
      // requested width is finer than the decoded data supports (short or
      // coarsely-sampled files in a wide lane). Catch rather than
      // predicting the library's internal minimum-scale threshold, which
      // would rot if that implementation detail changes.
      try {
        const floorWidth = Math.floor(width);
        const channel = data.resample({ width: floorWidth }).channel(0);
        const min: number[] = [];
        const max: number[] = [];

        for (let i = 0; i < floorWidth; i += 1) {
          min.push(channel.min_sample(i) / 128);
          max.push(channel.max_sample(i) / 128);
        }

        return { min, max };
      } catch (error) {
        if (!loggedZoomErrorRef.current) {
          loggedZoomErrorRef.current = true;
          // eslint-disable-next-line no-console
          console.debug(
            "[usePeaks] resample() could not render at this width",
            error
          );
        }
        return null;
      }
    },
    [status]
  );

  return { status, resample };
};
